import React, { useMemo } from 'react';
import Currency from 'react-currency-formatter';

// material-ui
import { TableCell, TableRow } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useLanguageReference } from 'services/Hooks/useLanguageReference';
import { useAllInventoryItems } from 'services/Data/InventoryItemServices';
import { useCeiling } from 'services/Data/ProjectServices';
import { arrayUnique } from 'services/Utilities/Utilities';
import { ProjectInventoryItemAdjustmentTypeEnumValue } from 'enums/ProjectInventoryItemAdjustmentTypeEnum';

// Icons

// Styles

// Actions

// Components

// Others

const useStyles = makeStyles(theme => ({
  //...customGlobalStyle(theme),
  ceilingCell: {
    maxWidth: '180px',
    textTransform: 'uppercase',
    borderRight: '1px solid rgba(224, 224, 224, 1)',
    fontSize: 'medium',
  },
  packagePrice: {
    borderLeft: '1px solid rgba(224, 224, 224, 1)',
    fontSize: 'medium',
    textAlign: 'center',
  },
  tableRow: {
    '& td': {
      padding: '10px',
    },
  },
}));

const DealerCopyAdjustmentGroupedCeilingSection = ({
  categoryLabel,
  projectInventoryItems,
  ceilingId,
  headerColSpan,
  projectAdjustments = [],
  currencyName,
}) => {
  const classes = useStyles();

  const { descriptionReference, nameReference } = useLanguageReference();
  const { data: availableInventoryItems } = useAllInventoryItems();

  const { data: ceiling, isLoading: isLoadingCeiling } = useCeiling(ceilingId);

  const addtionalItemsTotalForSection = projectInventoryItems.reduce((acc, cur) => {
    const currentAdjustment = projectAdjustments.find(pa => pa.inventoryItem_Id === cur.inventoryItem_Id);

    return acc + currentAdjustment.quantity * cur.projectDealerUnitPrice;
  }, 0);

  const uniqueInventoryItemSkus = useMemo(() => arrayUnique(projectAdjustments.map(pa => pa.inventoryItem_Id)), [
    projectAdjustments,
  ]);

  if (isLoadingCeiling) return null;

  return (
    <>
      <TableRow>
        <TableCell colSpan={headerColSpan} style={{ backgroundColor: '#c9c7c7' }}>
          <strong>{categoryLabel}</strong>
        </TableCell>
      </TableRow>

      {uniqueInventoryItemSkus?.map((inventoryItemId, index) => {
        const associatedProjectAjustements = projectAdjustments.filter(pa => pa.inventoryItem_Id === inventoryItemId);

        const firstProjectAjustement = associatedProjectAjustements[0];

        const totalQuantity = associatedProjectAjustements.reduce((acc, cur) => acc + (cur.quantity || 0), 0);

        const ceilingInventoryItem = projectInventoryItems.find(pi => pi.inventoryItem_Id === inventoryItemId);

        return (
          <TableRow
            className={classes.tableRow}
            key={ceilingInventoryItem.id}
            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
            {index === 0 && (
              <TableCell colSpan={2} className={classes.ceilingCell} rowSpan={uniqueInventoryItemSkus.length}>
                {`${ceiling?.[nameReference]}`}
                <br />
              </TableCell>
            )}
            <TableCell>
              <strong>{`${
                availableInventoryItems?.find(as => as.id === ceilingInventoryItem.inventoryItem_Id)?.sku
              }`}</strong>
            </TableCell>
            <TableCell colSpan={2}>{ceilingInventoryItem?.[descriptionReference]}</TableCell>
            {firstProjectAjustement ? (
              <TableCell align="center">
                {(firstProjectAjustement.adjustmentType === ProjectInventoryItemAdjustmentTypeEnumValue.DealerInventory
                  ? Math.abs(totalQuantity)
                  : totalQuantity) || '0'}
              </TableCell>
            ) : (
              <TableCell>-</TableCell>
            )}

            <TableCell align="right">
              {ceilingInventoryItem.projectDealerUnitPrice === 0 ? (
                '-'
              ) : (
                <Currency quantity={ceilingInventoryItem.projectDealerUnitPrice} currency={currencyName} />
              )}
            </TableCell>

            {firstProjectAjustement ? (
              <TableCell align="center">
                <Currency
                  quantity={(totalQuantity || 0) * ceilingInventoryItem.projectDealerUnitPrice}
                  currency={currencyName}
                />
              </TableCell>
            ) : (
              <TableCell>-</TableCell>
            )}

            {index === 0 && (
              <TableCell className={classes.packagePrice} rowSpan={uniqueInventoryItemSkus.length}>
                <Currency quantity={addtionalItemsTotalForSection} currency={currencyName} />
              </TableCell>
            )}
          </TableRow>
        );
      })}
    </>
  );
};

export default DealerCopyAdjustmentGroupedCeilingSection;
